import React, { ComponentProps, useMemo } from 'react';
import { CombinedSales } from '../../graphql/entities';

import { BaseSalesOrderWorkspace } from './SalesOrderWorkspace';
import Formatters from '../../components/grid-addons/formatters';
import { IWorkspaceColumnOverride } from '../../@types/workspaceTypes';
import { Paths } from '../../@types/keyOfNested';
import { ISalesOrder } from '../../graphql/schema/p4types';

const COMBINED_COLUMN_OVERRIDES: IWorkspaceColumnOverride = {
  orderNumber: {
    name: 'Order/Quote No',
    key: 'orderNumber',
    width: 100,
  },
  createDate: {
    name: 'Order Date',
    key: 'createDate',
    formatter: 'date',
    width: 120,
  },
  'customer.account.name': {
    name: 'Customer',
  },
  goodsTotal: {
    name: 'Goods Total',
    cellClass: 'rightAlign',
  },
  orderType: {
    name: 'Type',
    width: 100,
  },
  salesOrderStatus: {
    name: 'Status',
    sortable: false,
  },
  targetDate: {
    name: 'Expected Delivery Date',
  },
  vatTotal: {
    name: 'VAT Total',
  },
  baseVatTotal: {
    name: 'Base VAT Total',
  },
  'vatBusinessCategory.description': {
    name: 'VAT Business Category - Description',
  },
  amazonRefunded: {
    formatter: Formatters.AmazonRefundFormatter,
    name: 'Amazon Refund',
  },
  amazonRefundTotal: {
    name: 'Amazon Refund Total',
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
  },
  comment: {
    formatter: Formatters.CommentFormatter,
  },
};

export default (props: Partial<ComponentProps<typeof BaseSalesOrderWorkspace>>) => {
  const columnOverrides = useMemo(() => {
    const passedOverrides: IWorkspaceColumnOverride = props.columnOverrides ?? {};
    return {
      ...COMBINED_COLUMN_OVERRIDES,
      ...passedOverrides,
    };
  }, [props.columnOverrides]);

  const fieldNames: Array<Paths<ISalesOrder>> = useMemo(() => {
    const passedFields = props.fieldNames ? props.fieldNames : [];
    if (props.fieldNameOverrides) {
      return props.fieldNameOverrides;
    }
    //Despite these 2 arrays having identical types, TS complains that the union type is too complex, so ts-ignore here is to keep it quiet...

    let combinedFieldNames = [
      'orderNumber',
      'createdAt',
      'customer.reference',
      'customer.name',
      'orderReference',
      'headerTotal',
      'orderType',
      'salesOrderStatus',
      ...passedFields,
    ];

    if (props?.additionalVariables?.excludeCustomer) {
      combinedFieldNames = combinedFieldNames.filter((field) => !field.includes('customer.'));
    }

    // @ts-ignore
    return [...combinedFieldNames];
  }, [props.fieldNames, props.additionalVariables?.excludeCustomer]);

  return (
    <BaseSalesOrderWorkspace
      entity={CombinedSales}
      viewScreenName="Sales/CombinedSales/Workspace"
      title={'Quotes & Orders'}
      columnOverrides={columnOverrides}
      fieldNames={fieldNames}
      {...props}
    />
  );
};
